import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LinkIcon from '@mui/icons-material/Link';
import './AddGrowthHackModal.scss';
import { fetchClientsByPrefix } from '../../services/api/growthHacksService.js';

const primarySectorMapping = {
  'Mobility, Aerospace & Defense': [
    'Aerospace & Defense',
    'Automotive',
    'tbd-MA&D',
    'Transportation'
  ]
};

const subsectorMapping = {
  'Aerospace & Defense': [
    'Aerospace & Defense Mfg'
  ],
  'Automotive': [
    'Auto Retail, Dealers, Distribution & Aftersales',
    'Automotive Finance',
    'Commercial Vehicle Manufacturers',
    'Component Suppliers',
    'Fleet, Rental & Leasing',
    'Mobility-as-a-Service',
    'Passenger Vehicles',
    'SAcct-Elim'
  ],
  'tbd-MA&D': [
    'tbd-MA&D'
  ],
  'Transportation': [
    'Airlines',
    'Logistics',
    'Public Transport Services',
    'Rail',
    'Shipping'
  ]
};

export default function AddGrowthHackModal({ open, onClose, onSubmit }) {

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    sector: '',
    subsector: '',
    subSubsector: '',
    client: '',
    revenueValue: ''
  });
  const [errors, setErrors] = useState({});
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [searchedClients, setSearchedClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [availableSubsectors, setAvailableSubsectors] = useState([]);
  const [availableSubSubsectors, setAvailableSubSubsectors] = useState([]);
  const [confirmSubSubsectorOpen, setConfirmSubSubsectorOpen] = useState(false);
  const [pendingSubSubsector, setPendingSubSubsector] = useState('');

  useEffect(() => {
    if (formData.sector && primarySectorMapping[formData.sector]) {
      setAvailableSubsectors(primarySectorMapping[formData.sector]);
    } else {
      setAvailableSubsectors([]);
    }
  }, [formData.sector]);

  useEffect(() => {
    if (formData.subsector && subsectorMapping[formData.subsector]) {
      setAvailableSubSubsectors(subsectorMapping[formData.subsector]);
    } else {
      setAvailableSubSubsectors([]);
    }
  }, [formData.subsector]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.client && !formData.subSubsector) {
      newErrors.client = 'Either Client Name or Sub-Subsector is required';
      newErrors.subSubsector = 'Either Client Name or Sub-Subsector is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    if (!formData.sector) {
      newErrors.sector = 'Growth Hack (Primary Sector) is required';
    }
    
    if (!formData.subsector) {
      newErrors.subsector = 'Subsector is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => {
      const newState = {
        ...prevState,
        [name]: value
      };
      
      if (name === 'sector') {
        newState.subsector = '';
        newState.subSubsector = '';
      }
      
      if (name === 'subsector') {
        newState.subSubsector = '';
      }
      
      return newState;
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({...errors, [name]: undefined});
    }
    
    if (name === 'sector') {
      setErrors(prevErrors => ({
        ...prevErrors,
        subsector: undefined,
        subSubsector: undefined
      }));
    }
    
    if (name === 'subsector') {
      setErrors(prevErrors => ({
        ...prevErrors,
        subSubsector: undefined
      }));
    }
    
    if ((name === 'client' || name === 'subSubsector') && value) {
      setErrors(prevErrors => ({
        ...prevErrors,
        client: undefined,
        subSubsector: undefined
      }));
    }
  };

  const handleSubSubsectorChange = (e) => {
    const { value } = e.target;
    if (!value) {
      // Allow clearing directly
      handleChange(e);
      return;
    }
    setPendingSubSubsector(value);
    setConfirmSubSubsectorOpen(true);
  };

  const confirmSubSubsector = () => {
    const syntheticEvent = { target: { name: 'subSubsector', value: pendingSubSubsector } };
    handleChange(syntheticEvent);
    setConfirmSubSubsectorOpen(false);
    setPendingSubSubsector('');
  };

  const cancelSubSubsector = () => {
    setFormData(prev => ({ ...prev, subSubsector: '' }));
    setConfirmSubSubsectorOpen(false);
    setPendingSubSubsector('');
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      sector: '',
      subsector: '',
      subSubsector: '',
      client: '',
      revenueValue: ''
    });
    setErrors({});
    setSelectedClients([]);
    setClientSearchInput('');
    setSearchedClients([]);
    setAvailableSubsectors([]);
    setAvailableSubSubsectors([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onClose();
      onSubmit(formData);
      resetForm();
    }
  };

  const handleIncrement = () => {
    setFormData(prev => ({
      ...prev,
      revenueValue: prev.revenueValue ? (parseInt(prev.revenueValue) + 1).toString() : '1'
    }));
  };

  const handleDecrement = () => {
    setFormData(prev => ({
      ...prev,
      revenueValue: prev.revenueValue && parseInt(prev.revenueValue) > 0 
        ? (parseInt(prev.revenueValue) - 1).toString() 
        : '0'
    }));
  };

  // Client search (dynamic, like Filter Pane)
  const handleClientSearch = async (searchValue) => {
    setClientSearchInput(searchValue);

    if (searchValue.length >= 3) {
      setIsLoadingClients(true);
      try {
        const response = await fetchClientsByPrefix(
          searchValue,
          formData.sector,
          formData.subsector,
          "No"
        );
        if (response.success && response.data && Array.isArray(response.data)) {
          const clientOptions = response.data.map(client => ({
            label: String(client.ClientName || client.client_name || client.name || String(client)),
            value: String(client.ClientID || client.client_id || client.id || client)
          }));
          setSearchedClients(clientOptions);
        } else {
          setSearchedClients([]);
        }
      } catch (error) {
        console.error('Error searching clients:', error);
        setSearchedClients([]);
      } finally {
        setIsLoadingClients(false);
      }
    } else {
      setSearchedClients([]);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'growth-hack-modal'
      }}
    >
      <DialogTitle className="modal-header" sx={{ p: 0 }}>
        <Box className="modal-title-container">
          <Box component="span" className="modal-title">Add Growth Hack</Box>
          <IconButton onClick={handleClose} className="close-button">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box className="modal-form">
          <Box className="form-group">
            <Typography variant="subtitle1" className="form-label">Growth Hack Title *</Typography>
            <TextField
              fullWidth
              placeholder="Enter Name"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="form-input"
              variant="outlined"
              error={!!errors.title}
              helperText={errors.title}
            />
          </Box>

          <Box className="form-group">
            <Typography variant="subtitle1" className="form-label">Growth Hack Description *</Typography>
            <TextField
              fullWidth
              placeholder="Enter a description..."
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="form-input"
              variant="outlined"
              multiline
              rows={4}
              error={!!errors.description}
              helperText={errors.description}
            />
          </Box>

          <Box className="form-row">
            <Box className="form-group" sx={{ width: '48%' }}>
              <Typography variant="subtitle1" className="form-label">Growth Hack Type *</Typography>
              <FormControl fullWidth className="form-input" error={!!errors.category}>
                <Select
                  value={formData.category}
                  onChange={handleChange}
                  name="category"
                  displayEmpty
                  renderValue={formData.category ? undefined : () => "Select Type"}
                >
                  <MenuItem value="Annual/Quarterly reports (Strategy, Risks)">Annual/Quarterly reports (Strategy, Risks)</MenuItem>
                  <MenuItem value="Deals">Deals</MenuItem>
                  <MenuItem value="Financial (Segment) performance">Financial (Segment) performance</MenuItem>
                  <MenuItem value="Intelligence">Intelligence</MenuItem>
                  <MenuItem value="Key Developments">Key Developments</MenuItem>
                  <MenuItem value="Recent Wins">Recent Wins</MenuItem>
                </Select>
                {errors.category && <Typography color="error" variant="caption" sx={{ mt: 1 }}>{errors.category}</Typography>}
              </FormControl>
            </Box>
            {/* Sub Category field removed per request */}
          

          <Box className="form-group" sx={{ width: '48%' }}>
            <Typography variant="subtitle1" className="form-label">Revenue Potential</Typography>
            <Box className="revenue-input">
              <TextField
                placeholder="Enter value"
                name="revenueValue"
                value={formData.revenueValue}
                onChange={handleChange}
                className="form-input"
                variant="outlined"
              />
              <Box className="revenue-controls">
                <Button onClick={handleIncrement} className="increment-btn">
                  <AddIcon />
                </Button>
                <Button onClick={handleDecrement} className="decrement-btn">
                  <RemoveIcon />
                </Button>
              </Box>
            </Box>
          </Box>
          </Box>

          <Box className="form-row">
            <Box className="form-group" sx={{ width: '48%' }}>
              <Typography variant="subtitle1" className="form-label">Growth Hack (Primary Sector) *</Typography>
              <FormControl fullWidth className="form-input" error={!!errors.sector}>
                <Select
                  value={formData.sector}
                  onChange={handleChange}
                  name="sector"
                  displayEmpty
                  renderValue={
                    formData.sector
                      ? undefined
                      : () => (
                          <span style={{ color: '#fff' }}>Select Primary Sector</span>
                        )
                  }
                >                 
                  <MenuItem value="Mobility, Aerospace & Defense">Mobility, Aerospace & Defense</MenuItem>                 
                </Select>
                {errors.sector && <Typography color="error" variant="caption" sx={{ mt: 1 }}>{errors.sector}</Typography>}
              </FormControl>
            </Box>

            <Box className="form-group" sx={{ width: '48%' }}>
              <Typography variant="subtitle1" className="form-label">Subsector *</Typography>
              <FormControl fullWidth className="form-input" error={!!errors.subsector}>
                <Select
                  value={formData.subsector}
                  onChange={handleChange}
                  name="subsector"
                  displayEmpty
                  disabled={!formData.sector || availableSubsectors.length === 0}
                  renderValue={
                    formData.subsector
                      ? undefined
                      : () => (
                          <span style={{ color: '#fff' }}>
                            {formData.sector ? 'Select Subsector' : 'Select Primary Sector'}
                          </span>
                        )
                  }
                >
                  {availableSubsectors.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {errors.subsector && <Typography color="error" variant="caption" sx={{ mt: 1 }}>{errors.subsector}</Typography>}
              </FormControl>
            </Box>
          </Box>

          <Box className="grouped-section">
            <Box className="form-group">
              <Typography variant="subtitle1" className="form-label">Sub-Subsector *</Typography>
              <FormControl fullWidth className="form-input" error={!!errors.subSubsector}>
                <Select
                  value={formData.subSubsector}
                  onChange={handleSubSubsectorChange}
                  name="subSubsector"
                  displayEmpty
                  disabled={!formData.subsector || availableSubSubsectors.length === 0}
                  renderValue={
                    formData.subSubsector
                      ? undefined
                      : () => (
                          <span style={{ color: '#fff' }}>
                            {formData.subsector ? 'Select Sub-Subsector' : 'Select Subsector'}
                          </span>
                        )
                  }
                >
                  {availableSubSubsectors.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {errors.subSubsector && <Typography color="error" variant="caption" sx={{ mt: 1 }}>{errors.subSubsector}</Typography>}
              </FormControl>
            </Box>

            <Box className="or-divider">
              <Typography variant="body2" className="or-text">OR</Typography>
            </Box>
            <Box className="form-group">
              <Typography variant="subtitle1" className="form-label">Client Name *</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Autocomplete
                  multiple
          fullWidth
          sx={{ width: '100%' }}
                  options={searchedClients}
                  value={selectedClients}
                  open={clientSearchInput.length >= 3 && isClientsDropdownOpen}
                  onOpen={() => setIsClientsDropdownOpen(true)}
                  onClose={() => setIsClientsDropdownOpen(false)}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option && typeof option === 'object' && option.label) return String(option.label);
                    return '';
                  }}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  inputValue={clientSearchInput}
                  onInputChange={(event, newInputValue) => {
                    handleClientSearch(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    // newValue is an array of selected option objects
                    setSelectedClients(newValue || []);
                    const clientIdsCsv = (newValue || [])
                      .map(opt => String(opt.value))
                      .join(',');
                    setFormData(prev => ({ ...prev, client: clientIdsCsv }));
                    if ((newValue || []).length > 0) {
                      setErrors(prevErrors => ({ ...prevErrors, client: undefined, subSubsector: undefined }));
                    }
                  }}
                  loading={isLoadingClients}
                  noOptionsText={
                    clientSearchInput.length < 3 ? 'Type at least 3 characters to search' : 'No clients found'
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="Select"
                      error={!!errors.client}
                      helperText={
                        errors.client
                          ? errors.client
                          : clientSearchInput.length > 0 && clientSearchInput.length < 3
                          ? 'Enter at least 3 characters to search'
                          : selectedClients.length > 0
                          ? `${selectedClients.length} selected`
                          : ''
                      }
                      className="form-input"
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          <Box className="modal-actions">
            <Box className="left-actions">
              <Button
                variant="contained"
                className="link-btn"
                startIcon={<LinkIcon />}
                disabled
                aria-disabled="true"
              >
                Link
              </Button>
              <IconButton className="plus-btn" size="small" aria-label="add link" disabled aria-disabled="true">
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box className="right-actions">
              <Button onClick={handleClose} className="cancel-btn">
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSubmit}
                className="submit-btn"
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
     
      <Dialog
        open={confirmSubSubsectorOpen}
        onClose={cancelSubSubsector}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: 'confirm-subsubsector-dialog' }}
      >
        <DialogTitle>Confirm Sub-Subsector Selection</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to add this Growth Hack to all the companies within the '{pendingSubSubsector}' Sub-Subsector?
          </Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={cancelSubSubsector} className="cancel-btn">Cancel</Button>
            <Button variant="contained" onClick={confirmSubSubsector} className="confirm-btn">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
